import {
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  Modal,
  PropsModal,
  SuccessToast,
  pauseAllBrandProviders,
  pauseAllProviders,
  pauseAllProvidersBrands,
  reportError,
} from '@vgrubs/components';
import { ProviderAndProviderStatusResponse } from 'components/src/types/api/location.types';
import { ProviderStatus } from 'components/src/types/shared.types';
import { useEffect } from 'react';
import { scheduledPauseStartLabel } from './providerPause';
import { ProviderPauseOptions } from './ProviderPauseOptions';
import { ScheduledPauseCards } from './ScheduledPauseCards';
import { useProviderPause } from './useProviderPause';
import { ScheduledPausesState } from './useScheduledPauses';

export const PauseAllProvidersModal = ({
  modalIsOpen,
  closeModal,
  setLoading,
  setStatuses,
  statuses,
  id,
  isLocations = true,
  timeZone,
  scheduled,
  onPausedAll,
}: PropsModal & {
  setStatuses: (value: ProviderAndProviderStatusResponse) => void;
  modalIsOpen: boolean;
  id: string | number;
  isLocations?: boolean;
  timeZone?: string;
  statuses: ProviderAndProviderStatusResponse;
  scheduled: ScheduledPausesState;
  onPausedAll?: () => void;
}) => {
  const pause = useProviderPause(modalIsOpen, timeZone);
  const scheduledPauses = scheduled.of(null);

  // A merchant hears nothing when a scheduled pause starts, so the list is
  // fetched again each time the dialog opens.
  useEffect(() => {
    if (modalIsOpen) scheduled.refresh();
  }, [modalIsOpen]);

  async function handlePauseAll() {
    if (pause.error) return ErrorToast(pause.error);
    const { length } = pause;
    try {
      setLoading?.(true);
      const res = await (isLocations
        ? pauseAllProviders(id, length)
        : pauseAllBrandProviders(id, length));

      if (pause.isScheduled) {
        // Nothing changes on the providers until the pause starts.
        await scheduled.refresh();
        SuccessToast(`Pause scheduled: ${pause.rangeLabel}`);
        closeModal?.();
        setLoading?.(false);
        return;
      }

      setStatuses(
        statuses.map((item) => ({
          ...item,
          status: ProviderStatus.offline,
        }))
      );

      onPausedAll?.();

      SuccessToast(res.data.message);
      closeModal?.();

      setLoading?.(false);
    } catch (error) {
      reportError(error);
      setLoading?.(false);
    }
  }

  return (
    <Modal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header="Select Pause Time"
    >
      <ScheduledPauseCards
        pauses={scheduledPauses}
        timeZone={timeZone}
        onCancel={scheduled.cancel}
      />

      <ProviderPauseOptions pause={pause} timeZone={timeZone} />

      {scheduledPauses[0] && !pause.isScheduled && (
        <Disclaimer $m="16px 0 0 0" $center>
          Store stays online until{' '}
          {scheduledPauseStartLabel(scheduledPauses[0], timeZone)}.
        </Disclaimer>
      )}

      <Div $flex $column $gap="8px" $mt="40">
        <Button $selected $w="100%" onClick={() => handlePauseAll()}>
          {pause.confirmLabel ?? 'Pause'}
        </Button>

        <Button $selected $gray $w="100%" onClick={() => closeModal?.()}>
          Cancel
        </Button>
      </Div>
    </Modal>
  );
};
